import { useEffect, useRef, useState, type CSSProperties } from "react";
import ConfirmPage from "./pages/ConfirmPage";
import { pathnameContains } from "./utils/url";
import BookFlow from "./BookFlow";
import { STRINGS, normalizeLang, type Lang } from "./i18n";

// Onthoudt de taalkeuze van de bezoeker op de site van de klant zelf (dus per website,
// niet gedeeld tussen klanten) — zo hoeft iemand die eenmaal naar EN geschakeld heeft
// dat niet op elke pagina opnieuw te doen.
const LANG_STORAGE_KEY = "bookit-widget-lang";

function readStoredLang(): Lang | null {
    try {
        const stored = localStorage.getItem(LANG_STORAGE_KEY);
        return stored === "nl" || stored === "en" ? stored : null;
    } catch {
        return null;
    }
}

// Moet gelijk lopen aan de duur van .bk-modal.bk-closing / .bk-overlay.bk-closing
// in styles.ts — de DOM blijft dit lang gemount zodat de exit-animatie kan spelen.
const CLOSE_ANIMATION_MS = 180;

type WidgetProps = {
    companySlug: string;
    apiBase: string;
    mode: "inline" | "popup";
    lang?: string;
    buttonText?: string;
    primary?: string;
    accent?: string;
    buttonTextColor?: string;

    radius?: string;
    width?: string;

    position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
    offsetX?: string;
    offsetY?: string;

    fabRadius?: string;
    fabWidth?: string;
    fabHeight?: string;
    fabPadding?: string;

    /** Turnstile-token — alleen meegegeven vanaf de eigen /boek/{bedrijf}-pagina
     * (boekkoek.nl); een embed op een klant-site geeft dit niet mee. */
    captchaToken?: string;
};

export default function Widget({
                                   companySlug,
                                   apiBase,
                                   mode,
                                   lang = "nl",
                                   buttonText = "Boek nu",
                                   primary = "#111827",
                                   accent = "#22c55e",
                                   buttonTextColor = "#ffffff",
                                   radius = "16px",
                                   width = "520px",
                                   position = "bottom-right",
                                   offsetX = "24px",
                                   offsetY = "24px",
                                   fabRadius = "999px",
                                   fabWidth = "auto",
                                   fabHeight = "auto",
                                   fabPadding = "12px 16px",
                                   captchaToken,

                               }: WidgetProps) {
    const [open, setOpen] = useState(mode === "inline");
    const [closing, setClosing] = useState(false);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isConfirm = pathnameContains("/confirm");

    // Bezoeker-gekozen taal wint boven het lang-attribuut zodra die op deze site al eens
    // gewisseld is; anders begint de widget in de taal die de klant heeft ingesteld.
    const [uiLang, setUiLang] = useState<Lang>(() => readStoredLang() ?? normalizeLang(lang));
    const changeLang = (next: Lang) => {
        setUiLang(next);
        try {
            localStorage.setItem(LANG_STORAGE_KEY, next);
        } catch {
            // Stil falen — taal blijft dan alleen voor deze sessie gekozen.
        }
    };
    const modalT = STRINGS[uiLang];

    if (isConfirm) return <ConfirmPage />;

    // Speelt eerst de exit-animatie (bk-closing) af en unmount de modal pas daarna —
    // voorkomt dat 'ie abrupt verdwijnt in plaats van wegfadet/-schaalt.
    const handleClose = () => {
        if (!open || closing) return;
        setClosing(true);
        closeTimeoutRef.current = setTimeout(() => {
            setOpen(false);
            setClosing(false);
        }, CLOSE_ANIMATION_MS);
    };

    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        if (mode !== "popup" || !open) return;

        function onKey(e: KeyboardEvent) {
            if (e.key === "Escape") handleClose();
        }

        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [mode, open, closing]);

    useEffect(() => {
        if (mode !== "popup" || !open) return;

        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = prev;
        };
    }, [mode, open]);

    const widgetVars: CSSProperties = (() => {
        const base = {
            "--bk-primary": primary,
            "--bk-accent": accent,
            "--bk-radius": radius,
            "--bk-width": width,
            "--bk-button-text-color": buttonTextColor,

            "--bk-fab-radius": fabRadius,
            "--bk-fab-width": fabWidth,
            "--bk-fab-height": fabHeight,
            "--bk-fab-padding": fabPadding,
        } as CSSProperties;

        switch (position) {
            case "bottom-left":
                return {
                    ...base,
                    "--bk-modal-left": offsetX,
                    "--bk-modal-right": "auto",
                    "--bk-modal-bottom": offsetY,
                    "--bk-modal-top": "auto",
                    "--bk-fab-left": offsetX,
                    "--bk-fab-right": "auto",
                    "--bk-fab-bottom": offsetY,
                    "--bk-fab-top": "auto",
                } as CSSProperties;

            case "top-right":
                return {
                    ...base,
                    "--bk-modal-left": "auto",
                    "--bk-modal-right": offsetX,
                    "--bk-modal-bottom": "auto",
                    "--bk-modal-top": offsetY,
                    "--bk-fab-left": "auto",
                    "--bk-fab-right": offsetX,
                    "--bk-fab-bottom": "auto",
                    "--bk-fab-top": offsetY,
                } as CSSProperties;

            case "top-left":
                return {
                    ...base,
                    "--bk-modal-left": offsetX,
                    "--bk-modal-right": "auto",
                    "--bk-modal-bottom": "auto",
                    "--bk-modal-top": offsetY,
                    "--bk-fab-left": offsetX,
                    "--bk-fab-right": "auto",
                    "--bk-fab-bottom": "auto",
                    "--bk-fab-top": offsetY,
                } as CSSProperties;

            case "bottom-right":
            default:
                return {
                    ...base,
                    "--bk-modal-left": "auto",
                    "--bk-modal-right": offsetX,
                    "--bk-modal-bottom": offsetY,
                    "--bk-modal-top": "auto",
                    "--bk-fab-left": "auto",
                    "--bk-fab-right": offsetX,
                    "--bk-fab-bottom": offsetY,
                    "--bk-fab-top": "auto",
                } as CSSProperties;
        }
    })();

    if (mode === "inline") {
        return (
            <div className="bk-root bk-inline" style={widgetVars}>
                <BookFlow companySlug={companySlug} apiBase={apiBase} lang={uiLang} onLangChange={changeLang} captchaToken={captchaToken} />
            </div>
        );
    }

    return (
        <div className="bk-root" style={widgetVars}>
            <button
                className={`bk-fab${open ? " bk-fab-hidden" : ""}`}
                onClick={() => setOpen(true)}
                style={{ color: buttonTextColor }}
                aria-hidden={open}
                tabIndex={open ? -1 : 0}
            >
                {buttonText}
            </button>

            {(open || closing) && (
                <>
                    <div className={`bk-overlay${closing ? " bk-closing" : ""}`} onClick={handleClose} />

                    <div
                        className={`bk-modal${closing ? " bk-closing" : ""}`}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="bk-modal-header">
                            <div className="bk-title">
                                <strong>{modalT.modal.title}</strong>
                                <div className="bk-subtitle">{companySlug}</div>
                            </div>

                            <button
                                className="bk-close"
                                onClick={handleClose}
                                aria-label={modalT.modal.closeAria}
                                type="button"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="bk-modal-body">
                            <BookFlow
                                companySlug={companySlug}
                                apiBase={apiBase}
                                lang={uiLang}
                                onLangChange={changeLang}
                                onDone={handleClose}
                                captchaToken={captchaToken}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}